// Chat controls beside a thread: the per-message menu (reactions, emoji
// picker, reply/edit/unsend/remove/copy/report/block), the report dialog and
// the conversation settings panel (notify level, mute, clear, block/report in
// a DM).
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { api, errorMessage, unwrap } from "../api";
import {
  EMOJI_GROUPS,
  MUTE_CHOICES,
  canSetTimer,
  TIMER_CHOICES,
  timerLabel,
  muteText,
  muteUntil,
  myReaction,
  NOTIFY_CHOICES,
  QUICK_REACTIONS,
  REPORT_REASONS,
  type Conversation,
  type Message,
  type MessageActions,
  type ReportReason,
} from "../chat/chat";
import type { ChatStore } from "../chat/store";
import { useSetBlocked } from "../chat/useChatAccount";

/**
 * Closes a popover on Escape or a press outside `ref`. Presses on a toggle
 * (`data-popover-toggle`) are left to its own click, which would otherwise
 * reopen what the press just closed.
 */
function useDismiss(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    function onPointer(event: PointerEvent) {
      const target = event.target as Element;
      if (!ref.current || ref.current.contains(target) || target.closest?.("[data-popover-toggle]")) return;
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [ref, onClose]);
}

export interface MessageMenuHandlers {
  onReact: (emoji: string) => void;
  onReply: () => void;
  onEdit: () => void;
  onUnsend: () => void;
  onReport: () => void;
  onBlock: () => void;
  onPin: () => void;
  onUnpin: () => void;
  onClose: () => void;
}

/** The menu a message's "⋯" opens: quick reactions (and the picker), then what the caller may do. */
export function MessageMenu(props: {
  message: Message;
  selfId: string;
  senderName: string;
  actions: MessageActions;
  handlers: MessageMenuHandlers;
}) {
  const { message, selfId, senderName, actions, handlers } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [picker, setPicker] = useState(false);
  const mine = myReaction(message, selfId);
  useDismiss(ref, handlers.onClose);
  useEffect(() => ref.current?.querySelector<HTMLElement>("button")?.focus(), []);

  async function copy() {
    handlers.onClose();
    try {
      await navigator.clipboard.writeText(message.body ?? "");
    } catch {
      // Nothing to do: the text is still on screen.
    }
  }

  const item = (label: string, onClick: () => void, danger = false) => (
    <button type="button" role="menuitem" className={`chat-menu-item${danger ? " danger" : ""}`} onClick={onClick}>
      {label}
    </button>
  );

  return (
    <div className="chat-menu" ref={ref} role="menu" aria-label="Message actions">
      {actions.react && (
        <div className="chat-menu-reactions">
          {QUICK_REACTIONS.map((emoji) => (
            <EmojiButton key={emoji} emoji={emoji} selected={mine === emoji} onPick={handlers.onReact} />
          ))}
          <button
            type="button"
            className="chat-emoji more"
            aria-label="More reactions"
            aria-expanded={picker}
            onClick={() => setPicker((open) => !open)}
          >
            ＋
          </button>
        </div>
      )}
      {picker && <EmojiPicker selected={mine} onPick={handlers.onReact} />}
      {actions.reply && item("Reply", handlers.onReply)}
      {actions.edit && item("Edit", handlers.onEdit)}
      {message.body && item("Copy text", () => void copy())}
      {actions.pin && item("Pin", handlers.onPin)}
      {actions.unpin && item("Unpin", handlers.onUnpin)}
      {actions.unsend && item("Unsend", handlers.onUnsend, true)}
      {actions.remove && item("Remove for everyone", handlers.onUnsend, true)}
      {actions.report && item("Report message", handlers.onReport, true)}
      {actions.block && item(`Block ${senderName}`, handlers.onBlock, true)}
    </div>
  );
}

function EmojiButton(props: { emoji: string; selected: boolean; onPick: (emoji: string) => void }) {
  return (
    <button
      type="button"
      className={`chat-emoji${props.selected ? " selected" : ""}`}
      aria-label={props.selected ? `${props.emoji}, your reaction (take back)` : `React ${props.emoji}`}
      aria-pressed={props.selected}
      onClick={() => props.onPick(props.emoji)}
    >
      {props.emoji}
    </button>
  );
}

export function EmojiPicker(props: { selected: string | null; onPick: (emoji: string) => void }) {
  return (
    <div className="chat-picker">
      {EMOJI_GROUPS.map((group) => (
        <section key={group.name} aria-label={group.name}>
          <h3 className="hint">{group.name}</h3>
          <div className="chat-picker-grid">
            {group.emoji.map((emoji) => (
              <EmojiButton key={emoji} emoji={emoji} selected={props.selected === emoji} onPick={props.onPick} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------- report

export type ReportTarget =
  { kind: "message"; messageId: string; name: string } | { kind: "user"; userId: string; name: string };

/** Reports a message or a person to the server's operator; offers to block them too. */
export function ReportDialog(props: { target: ReportTarget; blockId: string | null; onClose: () => void }) {
  const { target, blockId, onClose } = props;
  const ref = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [note, setNote] = useState("");
  const [also, setAlso] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const setBlocked = useSetBlocked();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      const trimmed = note.trim();
      unwrap(
        await api.POST("/reports", {
          body: {
            ...(target.kind === "message" ? { message_id: target.messageId } : { user_id: target.userId }),
            reason,
            ...(trimmed ? { note: trimmed } : {}),
          },
        }),
      );
      if (also && blockId) await setBlocked.mutateAsync({ userId: blockId, blocked: true });
      setDone(true);
    } catch (err) {
      setError(
        errorMessage(err, {
          409: "That message was unsent, so there's nothing left to report.",
          429: "You've sent a lot of reports today. Try again tomorrow.",
        }),
      );
    } finally {
      setBusy(false);
    }
  }

  const title = target.kind === "message" ? `Report a message from ${target.name}` : `Report ${target.name}`;
  return (
    <dialog ref={ref} className="chat-dialog card" aria-label={title} onClose={onClose}>
      {done ? (
        <div className="stack">
          <h2>Thanks for telling us</h2>
          <p>The operator of this server will look at it. {target.name} isn't told who reported them.</p>
          <div className="button-row">
            <button type="button" onClick={onClose} autoFocus>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form className="stack" onSubmit={(event) => void submit(event)}>
          <h2>{title}</h2>
          <fieldset className="chat-reasons">
            <legend>What's wrong?</legend>
            {REPORT_REASONS.map((r) => (
              <label key={r.value} className="checkbox">
                <input
                  type="radio"
                  name="reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                />
                {r.label}
              </label>
            ))}
          </fieldset>
          <label>
            <span>Anything else? (optional)</span>
            <textarea value={note} maxLength={1000} rows={3} onChange={(event) => setNote(event.target.value)} />
          </label>
          {blockId && (
            <label className="checkbox">
              <input type="checkbox" checked={also} onChange={(event) => setAlso(event.target.checked)} />
              Also block {target.name}
            </label>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="button-row">
            <button type="submit" disabled={!reason || busy}>
              {busy ? "Sending…" : "Send report"}
            </button>
            <button type="button" className="secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}

// --------------------------------------------------------------- settings

/**
 * Notify level, mute and clear for this user; the disappearing-message timer
 * for everyone (set by either person in a DM, by admins elsewhere); in a DM
 * also block and report the other person.
 */
export function ConversationSettings(props: {
  conversation: Conversation;
  store: ChatStore;
  nowMs: number;
  /** The caller is an admin of the conversation's circle. */
  admin: boolean;
  peer: { id: string; name: string; blocked: boolean } | null;
  onReportPeer: () => void;
  onClose: () => void;
}) {
  const { conversation, store, nowMs, admin, peer, onReportPeer, onClose } = props;
  const timer = conversation.message_timer ?? null;
  const ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setBlocked = useSetBlocked();
  useDismiss(ref, onClose);
  const muted = muteText(conversation, nowMs);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function clear() {
    const question =
      "Clear this chat's history for you? Messages so far disappear on all your devices. Others keep theirs.";
    if (window.confirm(question)) void run(() => store.clear(conversation.id));
  }

  function toggleBlock() {
    if (!peer) return;
    if (!peer.blocked) {
      const question =
        `Block ${peer.name}? Neither of you can send messages in this chat, and you won't be notified about ` +
        "them. Circles you share, and location sharing, don't change.";
      if (!window.confirm(question)) return;
    }
    void run(() => setBlocked.mutateAsync({ userId: peer.id, blocked: !peer.blocked }));
  }

  return (
    <div className="chat-settings card" ref={ref} role="dialog" aria-label="Chat settings">
      <Section title="Notifications">
        {NOTIFY_CHOICES.map((choice) => (
          <label key={choice.value} className="checkbox">
            <input
              type="radio"
              name="notify"
              checked={conversation.notify === choice.value}
              disabled={busy}
              onChange={() => void run(() => store.updateSettings(conversation.id, { notify: choice.value }))}
            />
            {choice.label}
          </label>
        ))}
      </Section>
      <Section title="Mute">
        {muted ? (
          <div className="chat-settings-row">
            <span className="muted">{muted}</span>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => void run(() => store.updateSettings(conversation.id, { muted_until: null }))}
            >
              Unmute
            </button>
          </div>
        ) : (
          <div className="chat-settings-row">
            {MUTE_CHOICES.map((choice) => (
              <button
                key={choice.label}
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() =>
                  void run(() =>
                    store.updateSettings(conversation.id, { muted_until: muteUntil(choice.ms, Date.now()) }),
                  )
                }
              >
                {choice.label}
              </button>
            ))}
          </div>
        )}
      </Section>
      <Section title="Disappearing messages">
        {canSetTimer(conversation, admin) ? (
          <>
            {[{ value: null, label: "Off" }, ...TIMER_CHOICES].map((choice) => (
              <label key={choice.label} className="checkbox">
                <input
                  type="radio"
                  name="timer"
                  checked={timer === choice.value}
                  disabled={busy}
                  onChange={() => void run(() => store.setTimer(conversation.id, choice.value))}
                />
                {choice.label}
              </label>
            ))}
            <p className="hint">
              Applies to messages sent from now on, for everyone in this chat. Anyone can still copy or screenshot a
              message before it goes.
            </p>
          </>
        ) : (
          <p className="hint">
            {timer ? `New messages disappear after ${timerLabel(timer)}.` : "Off."}
            {conversation.kind !== "direct" && conversation.writable && " Only admins can change this."}
          </p>
        )}
      </Section>
      <Section title="History">
        <div className="chat-settings-row">
          <button type="button" className="secondary" disabled={busy} onClick={clear}>
            Clear history
          </button>
        </div>
      </Section>
      {peer && (
        <Section title={peer.name}>
          <div className="chat-settings-row">
            <button type="button" className="secondary" disabled={busy} onClick={toggleBlock}>
              {peer.blocked ? "Unblock" : "Block"}
            </button>
            <button type="button" className="secondary" onClick={onReportPeer}>
              Report
            </button>
          </div>
        </Section>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="chat-settings-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}
